/**
 * O domínio da tela de glosas (`TP-054`, T11) no front, em pt-BR.
 *
 * Diferente do faturamento, os TOKENS de enum de glosa já chegam em pt-BR
 * (`DbTokens.Glosa*`, `GlosaEndpoints.cs`): o mapeamento aqui só fecha o
 * vocabulário e tolera token desconhecido, nunca traduz idioma.
 *
 * A seção `*Api` abaixo é a forma CRUA, escrita à mão: espelha
 * `contracts/openapi/anery-api.v1.json` (schemas `GlosaResponse`,
 * `DemonstrativoResponse`, `DecidirGlosaRequest`, `DecisaoDeGlosaResponse`)
 * e o endpoint real (`GlosaEndpoints.cs`) linha a linha.
 */

// ------------------------------------------------------------- forma da API

/** Um item de `GET /glosas` — `GlosaResponse`. Os enums vêm como `string` livre no contrato. */
export interface GlosaApi {
  readonly id: string;
  readonly demonstrativoId: string;
  readonly linha: number;
  readonly guia: string;
  /** `null` = glosa da guia inteira, sem item (D13). */
  readonly codigo: string | null;
  readonly valorGlosado: number;
  readonly dataPagamento: string | null;
  readonly motivoCodigo: string | null;
  readonly causa: string | null;
  readonly tipo: string;
  readonly confianca: string;
  readonly decididaPor: string;
  readonly verificacao: number | null;
  readonly hipoteseRegraId: string | null;
  readonly hipoteseTrecho: string | null;
  readonly hipotesePagina: number | null;
  readonly regraConferidaId: string | null;
  readonly prazoRecursoAte: string | null;
  readonly ultimaDecisao: string | null;
  readonly tipoCorrigido: string | null;
  readonly protocolo?: string | null;
  /** `TP-067`: o trecho da regra que o motor citou na linha cobrada, e o da regra do prazo de recurso. */
  readonly regraConferidaTrecho?: string | null;
  readonly regraConferidaPagina?: number | null;
  readonly prazoRecursoTrecho?: string | null;
  readonly prazoRecursoPagina?: number | null;
  /** `TP-070`: o ticket que a área recebeu, ou o motivo de não ter recebido. Ausente em API antiga. */
  readonly roteamento?: RoteamentoApi | null;
}

/**
 * `GlosaEndpoints.RoteamentoResponse` (`TP-070`). Com ticket: `area`, `ticketId`, `ticketEstado` e `prazoEm` (nulo sem
 * data de pagamento). Sem ticket: `motivo`. Tudo nulo: a linha é elegível e o ticket ainda não foi aberto.
 */
export interface RoteamentoApi {
  readonly area: string | null;
  readonly ticketId: string | null;
  readonly ticketEstado: string | null;
  readonly prazoEm: string | null;
  readonly motivo: string | null;
}

/** O que `POST /glosas/demonstrativos` devolve — `DemonstrativoResponse`. */
export interface DemonstrativoApi {
  readonly demonstrativoId: string;
  readonly linhas: number;
  readonly porTipo: Readonly<Record<string, number>>;
  readonly confiancaBaixa: number;
  readonly semPar: number;
}

/** `POST /glosas/{id}/decisao` — `DecisaoDeGlosaResponse`. */
export interface DecisaoDeGlosaApi {
  readonly decisaoId: string;
}

// -------------------------------------------------------- domínio do front

/** `GlosaType`. Os tokens são os mesmos da API e do banco. */
export type TipoDeGlosa = 'administrativa' | 'tecnica' | 'fracionamento' | 'linear';

export const TIPOS_DE_GLOSA: readonly TipoDeGlosa[] = ['administrativa', 'tecnica', 'fracionamento', 'linear'];

export const ROTULOS_DE_TIPO: Record<TipoDeGlosa | 'desconhecido', string> = {
  administrativa: 'Administrativa',
  tecnica: 'Técnica',
  fracionamento: 'Fracionamento',
  linear: 'Linear',
  desconhecido: 'Tipo desconhecido',
};

/** `GlosaConfidence`. `desconhecido` existe porque o campo é `string` livre no contrato. */
export type Confianca = 'alta' | 'media' | 'baixa' | 'desconhecido';

export const ROTULOS_DE_CONFIANCA: Record<Confianca, string> = {
  alta: 'Alta',
  media: 'Média',
  baixa: 'Baixa',
  desconhecido: 'Desconhecida',
};

/** `GlosaCause` — o vocabulário fechado de causa (`clausulas-de-glosa-nos-contratos.md`). */
export const ROTULOS_DE_CAUSA: Readonly<Record<string, string>> = {
  cobranca_em_desacordo_com_tabela: 'Cobrança em desacordo com a tabela',
  carteira_fora_da_validade: 'Carteira fora da validade',
  sem_autorizacao_ou_cobertura: 'Sem autorização ou cobertura',
  conta_fora_do_prazo: 'Conta fora do prazo',
  nao_prescrito: 'Não prescrito',
  uso_nao_comprovado: 'Uso não comprovado',
  servico_divergente_do_documentado: 'Serviço divergente do documentado',
  fracionamento: 'Fracionamento',
};

/** `GlosaArea` (`TP-070`, `D2`): quem recebe o ticket da glosa técnica. Os tokens são os da API e do banco. */
export type AreaDeGlosa = 'relatorio_medico' | 'cadastro' | 'tabela' | 'farmacia';

export const AREAS_DE_GLOSA: readonly AreaDeGlosa[] = ['relatorio_medico', 'cadastro', 'tabela', 'farmacia'];

export const ROTULOS_DE_AREA: Record<AreaDeGlosa | 'desconhecida', string> = {
  relatorio_medico: 'Relatório médico',
  cadastro: 'Cadastro',
  tabela: 'Tabela',
  farmacia: 'Farmácia',
  desconhecida: 'Área desconhecida',
};

/** `GlosaRoutingReason`: por que uma linha técnica não virou ticket. `nao_e_tecnica` não aparece na tela. */
export type MotivoDeNaoRotear = 'nao_e_tecnica' | 'aguarda_confirmacao' | 'sem_caso' | 'sem_area_para_a_causa' | 'desconhecido';

export const ROTULOS_DE_MOTIVO: Record<MotivoDeNaoRotear, string> = {
  nao_e_tecnica: 'não é glosa técnica',
  aguarda_confirmacao: 'espera a confirmação na fila de revisão',
  sem_caso: 'a guia não tem par na conta fechada, então não há Caso',
  sem_area_para_a_causa: 'a causa ainda não tem área definida',
  desconhecido: 'motivo desconhecido',
};

/**
 * O roteamento de uma linha, já fechado no vocabulário. `roteada` tem ticket; `nao_roteada` tem o motivo; `pendente` é
 * a linha elegível cujo ticket ainda não foi aberto (o roteamento é refeito na próxima importação ou decisão).
 */
export type RoteamentoDaGlosa =
  | {
      readonly situacao: 'roteada';
      readonly area: AreaDeGlosa | 'desconhecida';
      readonly ticketId: string;
      readonly ticketEstado: string;
      /** Instante do prazo do recurso (ISO). `null` = sem data de pagamento. */
      readonly prazoEm: string | null;
    }
  | { readonly situacao: 'nao_roteada'; readonly motivo: MotivoDeNaoRotear }
  | { readonly situacao: 'pendente' };

/** `GlosaDecidedBy`: de onde saiu a causa (ordem `D7`). */
export type OrigemDaCausa = 'codigo_operadora' | 'verificacao_motor' | 'sem_causa' | 'desconhecido';

/** `GlosaDecisionAction`. */
export type AcaoDaDecisao = 'confirmar' | 'corrigir';

export interface Glosa {
  readonly id: string;
  readonly demonstrativoId: string;
  readonly linha: number;
  readonly guia: string;
  /** `null` = glosa da guia inteira, sem item (D13). */
  readonly codigo: string | null;
  /** Número do protocolo do lote (TISS, campo 10), quando o demonstrativo traz. */
  readonly protocolo: string | null;
  readonly valorGlosado: number;
  readonly dataPagamento: string | null;
  readonly motivoCodigo: string | null;
  /** Rótulo pronto da causa, ou `null` quando a glosa não tem causa (vira linear). */
  readonly causa: string | null;
  /** O tipo que o classificador deu. */
  readonly tipo: TipoDeGlosa | 'desconhecido';
  readonly confianca: Confianca;
  readonly origemDaCausa: OrigemDaCausa;
  readonly verificacao: number | null;
  readonly clausula: TrechoCitado | null;
  /** A regra do contrato que o motor citou na linha cobrada (`TP-067`, `D6`). */
  readonly regraConferida: TrechoCitado | null;
  readonly prazoRecursoAte: string | null;
  /** A cláusula de onde saiu o prazo do recurso (`TP-067`, `D6`). */
  readonly clausulaDoPrazo: TrechoCitado | null;
  readonly ultimaDecisao: AcaoDaDecisao | null;
  readonly tipoCorrigido: TipoDeGlosa | null;
  /** `null` = a API não mandou o roteamento. */
  readonly roteamento: RoteamentoDaGlosa | null;
}

/** Um trecho literal do contrato com a página. */
export interface TrechoCitado {
  readonly trecho: string;
  readonly pagina: number | null;
}

export interface DemonstrativoImportado {
  readonly demonstrativoId: string;
  readonly linhas: number;
  readonly confiancaBaixa: number;
  readonly semPar: number;
}

/**
 * O filtro da fila. `baixa` é a fila de revisão (o padrão da API sem filtro).
 * `todas` só existe com `demonstrativoId` ou com `area`: sem nenhum dos dois, a API cai na confiança baixa.
 * `area` (`TP-070`) lista só as linhas já roteadas para ela.
 * `situacao: 'pendentes'` deixa de fora a glosa já decidida: a confiança não
 * muda com a decisão, então sem isso a fila de revisão nunca esvazia.
 */
export interface FiltroDaFila {
  readonly confianca: Exclude<Confianca, 'desconhecido'> | 'todas';
  readonly tipo: TipoDeGlosa | '';
  readonly situacao: 'pendentes' | 'todas';
  readonly demonstrativoId: string | null;
  readonly area: AreaDeGlosa | '';
  /** As glosas que citaram esta hipótese do contrato, vindo da tela de Contratos (`TP-067`). */
  readonly hipoteseRegraId: string | null;
}
